'use client'

import { useState } from 'react'
import { CalendarDays, ClipboardList, Info, MessageSquare, Sparkles } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useTranslations } from 'next-intl'

interface AppInfoDialogProps {
  trigger?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const features = [
  { key: 'schedule', icon: CalendarDays },
  { key: 'activity_details', icon: ClipboardList },
  { key: 'growing', icon: Sparkles },
] as const

function AppInfoDialog({ trigger, open, onOpenChange }: AppInfoDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false)

  const t = useTranslations('app_info_dialog')

  const isControlled = open !== undefined
  const dialogOpen = isControlled ? open : internalOpen
  const setDialogOpen = isControlled ? onOpenChange : setInternalOpen

  const defaultTrigger = (
    <Button variant="link" className="h-auto p-0 text-sm text-gray-600">
      <Info className="mr-1 h-4 w-4" />
      {t('app_info')}
    </Button>
  )

  const content = (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <div className="flex items-center justify-center gap-2">
          <DialogTitle>{t('title')}</DialogTitle>
          <Badge variant="secondary" className="text-xs font-medium">
            {t('badge')}
          </Badge>
        </div>
        <DialogDescription asChild>
          <div className="flex flex-col gap-5 text-left">
            <p className="mt-1 text-center text-sm">{t('description')}</p>

            <ul className="space-y-4">
              {features.map(({ key, icon: Icon }) => (
                <li key={key} className="flex gap-3">
                  <div className="bg-primary/10 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                    <Icon className="text-primary h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">{t(`features.${key}.title`)}</h4>
                    <p className="text-sm text-gray-500">{t(`features.${key}.description`)}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex gap-2">
                <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('feedback_alert.title')}</p>
                  <p className="text-xs text-gray-500">{t('feedback_alert.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </DialogDescription>
      </DialogHeader>
    </DialogContent>
  )

  if (isControlled) {
    return (
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {content}
      </Dialog>
    )
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      {content}
    </Dialog>
  )
}

export { AppInfoDialog }
